import { useState } from 'react';
import { updateCampaignBudget, toggleCampaignStatus } from '../services/campaignService';

/**
 * Custom hook to manage campaign actions like budget updates and status changes
 */
const useCampaignActions = (showSuccess, showError, fetchReport, loadCampaignsData, loadFacebookCampaignsData, facebookAccountId = null, updateCampaignStatusLocally = null, updateCampaignBudgetLocally = null) => {
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [newBudget, setNewBudget] = useState("");
  const [updatingBudget, setUpdatingBudget] = useState(false);

  // Open budget dialog
  const openBudgetDialog = (campaign) => {
    if (!campaign || !campaign.platform) {
      showError('Invalid campaign data');
      return;
    }
    
    // Log campaign data to help debugging
    console.log('Opening budget dialog with campaign data:', campaign);
    
    let currentBudget = null;
    let updatedCampaign = { ...campaign };
    
    // Set initial budget value based on platform
    if (campaign.platform === 'taboola') {
      // For Taboola campaigns, check if we have the daily_cap directly in the campaign object
      currentBudget = campaign.daily_cap;
      
      // If not found in main campaign object and we have a taboolaCampaign object, try to get it from there
      if ((currentBudget === undefined || currentBudget === null) && campaign.taboolaCampaign) {
        currentBudget = campaign.taboolaCampaign.daily_cap;
        updatedCampaign.daily_cap = currentBudget;
      }
    } else if (campaign.platform === 'facebook') {
      // For Facebook campaigns, check if we have the daily_budget directly in the campaign object
      currentBudget = campaign.daily_budget;
      
      // If not found in main campaign object and we have a fbCampaign object, try to get it from there
      if ((currentBudget === undefined || currentBudget === null) && campaign.fbCampaign) {
        currentBudget = campaign.fbCampaign.daily_budget;
        updatedCampaign.daily_budget = currentBudget;
      }
    }
    
    // Make sure currentBudget is a valid number, otherwise set it to empty string
    const parsedBudget = currentBudget !== undefined && currentBudget !== null && !isNaN(parseFloat(currentBudget)) 
      ? parseFloat(currentBudget).toString() 
      : "";
    
    // Store the updated campaign data and budget value
    setSelectedCampaign(updatedCampaign);
    setNewBudget(parsedBudget);
    setBudgetDialogOpen(true);
  };

  // Close budget dialog
  const handleBudgetDialogClose = () => {
    setBudgetDialogOpen(false);
  };

  // Handle budget input change
  const handleBudgetChange = (e) => {
    setNewBudget(e.target.value);
  };

  // Update campaign budget
  const handleUpdateBudget = async () => {
    setUpdatingBudget(true);
    try {
      const result = await updateCampaignBudget(selectedCampaign, newBudget);
      
      // Close the dialog first before showing any messages or reloading data
      handleBudgetDialogClose();
      
      if (result.success) {
        showSuccess(`Budget updated to $${newBudget}`);

        // Immediately update the UI with the new budget value
        if (updateCampaignBudgetLocally) {
          updateCampaignBudgetLocally(selectedCampaign.campaign_id, selectedCampaign.platform, newBudget);
        }

        // Then reload campaign data in the background based on platform type
        if (selectedCampaign.platform === 'taboola') {
          console.log('Reloading Taboola campaigns data after budget update');
          loadCampaignsData(true);
        } else if (selectedCampaign.platform === 'facebook') {
          console.log('Reloading Facebook campaigns data after budget update');
          loadFacebookCampaignsData(true);
        }
      } else {
        showError(result.error || 'Failed to update budget');
      }
    } catch (error) {
      // Also close the dialog in case of errors
      handleBudgetDialogClose();
      console.error('Error updating budget:', error);
      showError(error.message || 'Failed to update budget');
    } finally {
      setUpdatingBudget(false);
    }
  };

  // Toggle campaign status (active/paused)
  const handleToggleCampaignStatus = async (campaign) => {
    if (!campaign || !campaign.platform) {
      showError('Invalid campaign data');
      return;
    }

    try {
      const response = await toggleCampaignStatus(campaign);

      if (response.success) {
        const statusText = response.newStatus ? 'activated' : 'paused';
        showSuccess(`${campaign.platform} campaign ${statusText} successfully`);

        // Update the campaign status locally without refreshing the entire table
        if (updateCampaignStatusLocally) {
          updateCampaignStatusLocally(campaign.campaign_id, campaign.platform, response.newStatus);
        }

        return response;
      } else {
        showError(response.error || 'Failed to update campaign status');
        return response;
      }
    } catch (error) {
      console.error('Error toggling campaign status:', error);
      showError(error.message || 'An unexpected error occurred');
      return { success: false, error: error.message };
    }
  };

  return {
    budgetDialogOpen,
    selectedCampaign,
    newBudget,
    openBudgetDialog,
    handleBudgetDialogClose,
    handleBudgetChange,
    handleUpdateBudget,
    handleToggleCampaignStatus
  };
};

export default useCampaignActions; 